'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Car, CheckCircle, XCircle, Navigation, Clock, DollarSign, Loader2, Bell, BellOff, ToggleLeft, ToggleRight, ChevronRight, Phone, Star, AlertTriangle, Zap,  } from 'lucide-react';
import RideChat from '@/components/RideChat';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────
interface Driver {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  status: string;
  is_online: boolean;
  rating: number | null;
  total_trips: number;
  total_earnings: number;
  current_lat: number | null;
  current_lng: number | null;
  company_id: string;
}

interface JobNotification {
  id: string;
  booking_id: string;
  status: string;
  expires_at: string;
  booking?: {
    pickup_address: string;
    dropoff_address: string;
    estimated_fare: number | null;
    passenger_id: string | null;
    booking_type?: string | null;
    passengers?: { full_name: string; phone?: string | null } | null;
  };
}

interface ActiveBooking {
  id: string;
  status: string;
  pickup_address: string;
  dropoff_address: string;
  estimated_fare: number | null;
  booking_type?: string | null;
  passengers?: { full_name: string; phone: string | null } | null;
  pickup_lat: number | null;
  pickup_lng: number | null;
  dropoff_lat: number | null;
  dropoff_lng: number | null;
}

interface TripHistory {
  id: string;
  status: string;
  pickup_address: string;
  dropoff_address: string;
  final_fare: number | null;
  estimated_fare: number | null;
  completed_at: string | null;
  created_at: string;
}

type DriverView = 'dashboard' | 'jobs' | 'active_trip' | 'earnings';

// ─── Confirmation Modal ───────────────────────────────────────────────────────
function ConfirmModal({
  type,
  job,
  onConfirm,
  onCancel,
  loading,
}: {
  type: 'accept' | 'decline';
  job: JobNotification;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const isAccept = type === 'accept';
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div
        className="w-full max-w-md rounded-2xl p-5 space-y-4 animate-in slide-in-from-bottom-4"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      >
        {/* Icon */}
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: isAccept ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
            }}
          >
            {isAccept
              ? <CheckCircle className="w-6 h-6" style={{ color: 'var(--success)' }} />
              : <XCircle className="w-6 h-6" style={{ color: 'var(--danger)' }} />
            }
          </div>
          <div>
            <p className="font-bold text-base">
              {isAccept ? 'Accept this job?' : 'Decline this job?'}
            </p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              {isAccept
                ? 'You will be assigned to this booking immediately.' :'The job will be offered to another driver.'}
            </p>
          </div>
        </div>

        {/* Job summary */}
        <div
          className="p-3.5 rounded-xl space-y-2"
          style={{ background: 'var(--muted)' }}
        >
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: 'var(--success)' }} />
            <div>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Pickup</p>
              <p className="text-sm font-medium">{job.booking?.pickup_address}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: 'var(--danger)' }} />
            <div>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Drop-off</p>
              <p className="text-sm font-medium">{job.booking?.dropoff_address}</p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Passenger: {job.booking?.passengers?.full_name || 'Unknown'}
            </span>
            <span className="font-bold text-sm" style={{ color: 'var(--primary)' }}>
              £{Number(job.booking?.estimated_fare || 0).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3 rounded-xl text-sm font-medium border transition-all"
            style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2"
            style={{
              background: isAccept ? 'var(--success)' : 'var(--danger)',
              color: '#fff',
            }}
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : isAccept
                ? <><CheckCircle className="w-4 h-4" />Accept Job</>
                : <><XCircle className="w-4 h-4" />Decline Job</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Job Card ─────────────────────────────────────────────────────────────────
function JobCard({
  job,
  onAccept,
  onDecline,
  actionLoading,
}: {
  job: JobNotification;
  onAccept: () => void;
  onDecline: () => void;
  actionLoading: boolean;
}) {
  const [timeLeft, setTimeLeft] = useState(
    Math.max(0, Math.floor((new Date(job.expires_at).getTime() - Date.now()) / 1000))
  );

  useEffect(() => {
    const t = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const pct = Math.min(100, (timeLeft / 60) * 100);
  const isUrgent = timeLeft < 15;

  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{
        background: 'var(--card)',
        borderColor: isUrgent ? 'var(--danger)' : 'var(--border)',
        borderWidth: isUrgent ? '2px' : '1px',
      }}
    >
      {/* Timer bar */}
      <div className="h-1.5 w-full" style={{ background: 'var(--muted)' }}>
        <div
          className="h-full transition-all duration-1000"
          style={{
            width: `${pct}%`,
            background: isUrgent ? 'var(--danger)' : 'var(--primary)',
          }}
        />
      </div>

      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm"
              style={{ background: 'rgba(245,158,11,0.12)', color: 'var(--primary)' }}
            >
              {(job.booking?.passengers?.full_name || 'P').charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-sm">
                {job.booking?.passengers?.full_name || 'Passenger'}
              </p>
              <p className="text-xs capitalize" style={{ color: 'var(--muted-foreground)' }}>
                {job.booking?.booking_type || 'Standard'} ride
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-bold text-lg" style={{ color: 'var(--primary)' }}>
              £{Number(job.booking?.estimated_fare || 0).toFixed(2)}
            </p>
            <p
              className="text-xs flex items-center gap-1 justify-end"
              style={{ color: isUrgent ? 'var(--danger)' : 'var(--muted-foreground)' }}
            >
              <Clock className="w-3 h-3" />
              {timeLeft}s
            </p>
          </div>
        </div>

        {/* Route */}
        <div
          className="p-3 rounded-xl space-y-2.5"
          style={{ background: 'var(--muted)' }}
        >
          <div className="flex items-start gap-2.5">
            <div className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0" style={{ background: 'var(--success)' }} />
            <div>
              <p className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>PICKUP</p>
              <p className="text-sm font-medium">{job.booking?.pickup_address}</p>
            </div>
          </div>
          <div className="ml-1 border-l-2 border-dashed h-3" style={{ borderColor: 'var(--border)' }} />
          <div className="flex items-start gap-2.5">
            <div className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0" style={{ background: 'var(--danger)' }} />
            <div>
              <p className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>DROP-OFF</p>
              <p className="text-sm font-medium">{job.booking?.dropoff_address}</p>
            </div>
          </div>
        </div>

        {/* ETA badge */}
        <div className="flex items-center gap-2">
          <span
            className="text-xs px-2.5 py-1 rounded-full flex items-center gap-1"
            style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--info)' }}
          >
            <Zap className="w-3 h-3" />
            ETA ~3 min
          </span>
          <span
            className="text-xs px-2.5 py-1 rounded-full"
            style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
          >
            #{job.booking_id.slice(0, 8)}
          </span>
        </div>

        {/* Buttons */}
        <div className="flex gap-2.5">
          <button
            onClick={onDecline}
            disabled={actionLoading || timeLeft === 0}
            className="flex-1 py-3 rounded-xl text-sm font-semibold border flex items-center justify-center gap-1.5 transition-all"
            style={{ borderColor: 'var(--danger)', color: 'var(--danger)', background: 'rgba(239,68,68,0.06)' }}
          >
            <XCircle className="w-4 h-4" />
            Decline
          </button>
          <button
            onClick={onAccept}
            disabled={actionLoading || timeLeft === 0}
            className="flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-all"
            style={{ background: 'var(--success)', color: '#fff' }}
          >
            {actionLoading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <><CheckCircle className="w-4 h-4" />Accept</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DriverApp() {
  const supabase = createClient();
  const [view, setView] = useState<DriverView>('dashboard');
  const [driverEmail, setDriverEmail] = useState('');
  const [driver, setDriver] = useState<Driver | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [locationGranted, setLocationGranted] = useState<boolean | null>(null);
  const [pendingJobs, setPendingJobs] = useState<JobNotification[]>([]);
  const [activeBooking, setActiveBooking] = useState<ActiveBooking | null>(null);
  const [tripHistory, setTripHistory] = useState<TripHistory[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [todayEarnings, setTodayEarnings] = useState(0);
  const [todayTrips, setTodayTrips] = useState(0);
  const [modal, setModal] = useState<{ type: 'accept' | 'decline'; job: JobNotification } | null>(null);
  const locationWatchRef = useRef<number | null>(null);
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchActiveBooking = useCallback(async (driverId: string) => {
    const { data } = await supabase
      .from('bookings')
      .select('*, passengers(full_name, phone)')
      .eq('driver_id', driverId)
      .in('status', ['accepted', 'driver_arriving', 'driver_arrived', 'in_progress'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setActiveBooking(data);
    if (data) setView('active_trip');
  }, []);

  const fetchPendingJobs = useCallback(async (driverId: string) => {
    const { data } = await supabase
      .from('driver_job_queue')
      .select('*, booking:bookings(pickup_address, dropoff_address, estimated_fare, passenger_id, booking_type, passengers(full_name, phone))')
      .eq('driver_id', driverId)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    setPendingJobs(data || []);
  }, []);

  const fetchTripHistory = useCallback(async (driverId: string, companyId: string) => {
    const { data } = await supabase
      .from('bookings')
      .select('id, status, pickup_address, dropoff_address, final_fare, estimated_fare, completed_at, created_at')
      .eq('driver_id', driverId)
      .eq('company_id', companyId)
      .in('status', ['completed', 'cancelled'])
      .order('created_at', { ascending: false })
      .limit(20);
    setTripHistory(data || []);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayData = (data || []).filter(
      (t) => t.status === 'completed' && t.completed_at && new Date(t.completed_at) >= today
    );
    setTodayTrips(todayData.length);
    setTodayEarnings(todayData.reduce((s, t) => s + Number(t.final_fare || t.estimated_fare || 0), 0));
  }, []);

  // ── Location permission ────────────────────────────────────────────────────
  const requestLocation = useCallback((driverId: string) => {
    if (!navigator.geolocation) { setLocationGranted(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationGranted(true);
        supabase.from('drivers').update({
          current_lat: pos.coords.latitude,
          current_lng: pos.coords.longitude,
          updated_at: new Date().toISOString(),
        }).eq('id', driverId);
      },
      () => setLocationGranted(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const { data: driverData, error } = await supabase
        .from('drivers')
        .select('*')
        .eq('email', driverEmail.trim())
        .maybeSingle();
      if (error || !driverData) {
        setLoginError('Driver account not found. Please contact your company admin.');
        return;
      }
      if (driverData.status === 'suspended') {
        setLoginError('Your account has been suspended. Please contact your company admin.');
        return;
      }
      setDriver(driverData);
      // Auto-request location on login
      requestLocation(driverData.id);
      fetchActiveBooking(driverData.id);
      fetchPendingJobs(driverData.id);
      fetchTripHistory(driverData.id, driverData.company_id);
    } catch {
      setLoginError('Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  // ── Toggle online ──────────────────────────────────────────────────────────
  const toggleOnline = async () => {
    if (!driver) return;
    const newOnline = !driver.is_online;
    const newStatus = newOnline ? 'online' : 'offline';
    await supabase
      .from('drivers')
      .update({ is_online: newOnline, status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', driver.id);
    setDriver((d) => d ? { ...d, is_online: newOnline, status: newStatus } : null);

    if (newOnline) {
      requestLocation(driver.id);
      if (navigator.geolocation) {
        locationWatchRef.current = navigator.geolocation.watchPosition(
          (pos) => {
            supabase.from('drivers').update({
              current_lat: pos.coords.latitude,
              current_lng: pos.coords.longitude,
              updated_at: new Date().toISOString(),
            }).eq('id', driver.id);
          },
          null,
          { enableHighAccuracy: true, maximumAge: 10000 }
        );
      }
    } else {
      if (locationWatchRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
        locationWatchRef.current = null;
      }
    }
  };

  // ── Real-time subscription ─────────────────────────────────────────────────
  useEffect(() => {
    if (!driver) return;

    // Clean up previous channel
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }

    const channel = supabase
      .channel(`driver-realtime-${driver.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'driver_job_queue',
        filter: `driver_id=eq.${driver.id}`,
      }, () => {
        fetchPendingJobs(driver.id);
        // Auto-switch to jobs view when new job arrives
        setView((v) => v === 'active_trip' ? v : 'jobs');
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'driver_job_queue',
        filter: `driver_id=eq.${driver.id}`,
      }, () => {
        fetchPendingJobs(driver.id);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'bookings',
        filter: `driver_id=eq.${driver.id}`,
      }, (payload) => {
        if (activeBooking && payload.new.id === activeBooking.id) {
          setActiveBooking((prev) => prev ? { ...prev, ...payload.new } : null);
        }
        // If booking was cancelled
        if (payload.new.status === 'cancelled' && activeBooking?.id === payload.new.id) {
          setActiveBooking(null);
          setView('dashboard');
        }
      })
      .subscribe();

    realtimeChannelRef.current = channel;
    return () => { supabase.removeChannel(channel); };
  }, [driver?.id, activeBooking?.id]);

  // ── Job response ───────────────────────────────────────────────────────────
  const handleJobResponse = async (job: JobNotification, response: 'accepted' | 'rejected') => {
    setActionLoading(true);
    setModal(null);
    try {
      const res = await fetch('/api/pwa/driver-response', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId: job.id, driverId: driver?.id, response, bookingId: job.booking_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      if (response === 'accepted' && data.assigned) {
        fetchActiveBooking(driver!.id);
        setPendingJobs([]);
      } else {
        fetchPendingJobs(driver!.id);
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to respond to job');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Trip actions ───────────────────────────────────────────────────────────
  const handleTripAction = async (action: string) => {
    if (!activeBooking || !driver) return;
    setActionLoading(true);
    try {
      let lat: number | undefined, lng: number | undefined;
      if (navigator.geolocation) {
        await new Promise<void>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => { lat = pos.coords.latitude; lng = pos.coords.longitude; resolve(); },
            () => resolve(),
            { timeout: 3000 }
          );
        });
      }
      const res = await fetch('/api/pwa/update-trip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingId: activeBooking.id, driverId: driver.id, action, lat, lng }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      if (action === 'complete_trip') {
        setActiveBooking(null);
        setView('dashboard');
        fetchTripHistory(driver.id, driver.company_id);
        setDriver((d) => d ? { ...d, status: 'online', total_trips: d.total_trips + 1 } : null);
      } else {
        setActiveBooking((prev) => prev ? { ...prev, status: data.status } : null);
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update trip');
    } finally {
      setActionLoading(false);
    }
  };

  // ── LOGIN SCREEN ───────────────────────────────────────────────────────────
  if (!driver) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
        <div
          className="px-4 py-3 border-b flex items-center gap-2"
          style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary)' }}>
            <Car className="w-4 h-4" style={{ color: 'var(--primary-foreground)' }} />
          </div>
          <span className="font-bold text-sm">RideOS Driver</span>
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-sm space-y-5">
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(245,158,11,0.15)' }}
              >
                <Car className="w-8 h-8" style={{ color: 'var(--primary)' }} />
              </div>
              <h1 className="text-2xl font-bold">Driver Login</h1>
              <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
                Sign in with your driver account
              </p>
            </div>

            {/* Location permission notice */}
            <div
              className="p-3 rounded-xl flex items-start gap-2.5 text-xs"
              style={{ background: 'rgba(59,130,246,0.08)', color: 'var(--info)' }}
            >
              <Navigation className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                <strong>Location access required.</strong> Your location will be requested automatically
                after login to enable job matching and navigation.
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-3">
              <div>
                <label className="label-field">Email Address</label>
                <input
                  type="email"
                  placeholder="driver@example.com"
                  value={driverEmail}
                  onChange={(e) => setDriverEmail(e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              {loginError && (
                <p
                  className="text-sm p-3 rounded-lg"
                  style={{ background: 'rgba(239,68,68,0.08)', color: 'var(--danger)' }}
                >
                  {loginError}
                </p>
              )}
              <button
                type="submit"
                disabled={loginLoading || !driverEmail}
                className="btn-primary w-full"
              >
                {loginLoading
                  ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Signing in...</span>
                  : 'Sign In & Enable Location'
                }
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN APP ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10 px-4 py-3 flex items-center gap-3 border-b"
        style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary)' }}>
          <Car className="w-4 h-4" style={{ color: 'var(--primary-foreground)' }} />
        </div>
        <span className="font-bold text-sm flex-1">RideOS Driver</span>

        {/* Location indicator */}
        {locationGranted !== null && (
          <div
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
            style={{
              background: locationGranted ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
              color: locationGranted ? 'var(--success)' : 'var(--danger)',
            }}
          >
            <Navigation className="w-3 h-3" />
            {locationGranted ? 'GPS' : 'No GPS'}
          </div>
        )}

        <button
          onClick={toggleOnline}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
          style={{
            background: driver.is_online ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)',
            color: driver.is_online ? 'var(--success)' : 'var(--muted-foreground)',
          }}
        >
          {driver.is_online ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
          {driver.is_online ? 'Online' : 'Offline'}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-md mx-auto w-full px-4 py-5 space-y-4">

        {/* ── DASHBOARD ──────────────────────────────────────────────────── */}
        {view === 'dashboard' && (
          <>
            <div>
              <h2 className="text-xl font-bold">Hello, {driver.full_name.split(' ')[0]}!</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                {driver.is_online
                  ? 'You are online and receiving job notifications'
                  : 'Go online to start receiving jobs'}
              </p>
            </div>

            {/* Location permission warning */}
            {locationGranted === false && (
              <div
                className="p-3.5 rounded-xl flex items-start gap-2.5"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--danger)' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--danger)' }}>Location access denied</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                    Enable location in your browser settings to receive jobs and navigate.
                  </p>
                  <button
                    onClick={() => requestLocation(driver.id)}
                    className="text-xs mt-1.5 underline"
                    style={{ color: 'var(--danger)' }}
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Today's Trips", value: todayTrips, icon: Car, color: 'var(--info)' },
                { label: "Today's Earnings", value: `£${todayEarnings.toFixed(2)}`, icon: DollarSign, color: 'var(--success)' },
                { label: 'Total Trips', value: driver.total_trips, icon: CheckCircle, color: 'var(--primary)' },
                { label: 'Rating', value: `${driver.rating || '5.0'} ★`, icon: Star, color: 'var(--warning)' },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="p-4 rounded-xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                  <Icon className="w-5 h-5 mb-2" style={{ color }} />
                  <p className="text-xl font-bold">{value}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{label}</p>
                </div>
              ))}
            </div>

            {/* Pending jobs alert */}
            {pendingJobs.length > 0 && (
              <button
                onClick={() => setView('jobs')}
                className="w-full p-4 rounded-xl border flex items-center gap-3"
                style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.3)' }}
              >
                <Bell className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                <div className="flex-1 text-left">
                  <p className="font-semibold text-sm" style={{ color: 'var(--primary)' }}>
                    {pendingJobs.length} New Job{pendingJobs.length > 1 ? 's' : ''} Available!
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Tap to view and accept</p>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              </button>
            )}

            {/* Active trip shortcut */}
            {activeBooking && (
              <button
                onClick={() => setView('active_trip')}
                className="w-full p-4 rounded-xl border flex items-center gap-3"
                style={{ background: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.3)' }}
              >
                <Navigation className="w-5 h-5" style={{ color: 'var(--success)' }} />
                <div className="flex-1 text-left">
                  <p className="font-semibold text-sm" style={{ color: 'var(--success)' }}>Active Trip</p>
                  <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>
                    {activeBooking.pickup_address}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: 'var(--success)' }} />
              </button>
            )}

            {!driver.is_online && !activeBooking && (
              <div
                className="p-4 rounded-xl border text-center"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              >
                <BellOff className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--muted-foreground)', opacity: 0.5 }} />
                <p className="text-sm font-medium">You are offline</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  Toggle online to receive job notifications
                </p>
              </div>
            )}
          </>
        )}

        {/* ── JOBS VIEW ──────────────────────────────────────────────────── */}
        {view === 'jobs' && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Incoming Jobs</h2>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {pendingJobs.length} pending · synced via Supabase real-time
                </p>
              </div>
              <button
                onClick={() => setView('dashboard')}
                className="text-xs px-3 py-1.5 rounded-lg"
                style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
              >
                Back
              </button>
            </div>

            {pendingJobs.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No pending jobs right now</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  New jobs will appear here automatically
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingJobs.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    onAccept={() => setModal({ type: 'accept', job })}
                    onDecline={() => setModal({ type: 'decline', job })}
                    actionLoading={actionLoading}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── ACTIVE TRIP ────────────────────────────────────────────────── */}
        {view === 'active_trip' && activeBooking && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Active Trip</h2>
                <p
                  className="text-sm capitalize font-medium"
                  style={{ color: 'var(--success)' }}
                >
                  {activeBooking.status.replace(/_/g, ' ')}
                </p>
              </div>
              <button
                onClick={() => setView('dashboard')}
                className="text-xs px-3 py-1.5 rounded-lg"
                style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
              >
                Dashboard
              </button>
            </div>

            {/* Route card */}
            <div
              className="p-4 rounded-2xl border space-y-3"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <div className="flex items-start gap-2.5">
                <div className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0" style={{ background: 'var(--success)' }} />
                <div>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Pickup</p>
                  <p className="font-medium text-sm">{activeBooking.pickup_address}</p>
                </div>
              </div>
              <div className="ml-1 border-l-2 border-dashed h-3" style={{ borderColor: 'var(--border)' }} />
              <div className="flex items-start gap-2.5">
                <div className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0" style={{ background: 'var(--danger)' }} />
                <div>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Drop-off</p>
                  <p className="font-medium text-sm">{activeBooking.dropoff_address}</p>
                </div>
              </div>
              <div
                className="flex justify-between pt-2 border-t"
                style={{ borderColor: 'var(--border)' }}
              >
                <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Fare</span>
                <span className="font-bold" style={{ color: 'var(--primary)' }}>
                  £{Number(activeBooking.estimated_fare || 0).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Passenger info */}
            {activeBooking.passengers && (
              <div
                className="p-4 rounded-2xl border flex items-center gap-3"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                  style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--primary)' }}
                >
                  {activeBooking.passengers.full_name.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{activeBooking.passengers.full_name}</p>
                  <p className="text-xs capitalize" style={{ color: 'var(--muted-foreground)' }}>
                    {activeBooking.booking_type || 'Standard'} ride
                  </p>
                </div>
                {activeBooking.passengers.phone && (
                  <a
                    href={`tel:${activeBooking.passengers.phone}`}
                    className="p-2.5 rounded-xl"
                    style={{ background: 'rgba(16,185,129,0.1)', color: 'var(--success)' }}
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}

            {/* Navigate */}
            {activeBooking.pickup_lat && activeBooking.pickup_lng && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${activeBooking.pickup_lat},${activeBooking.pickup_lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium border"
                style={{ background: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.3)', color: 'var(--info)' }}
              >
                <Navigation className="w-4 h-4" />
                Navigate to Pickup
              </a>
            )}

            {/* Trip action buttons */}
            <div className="space-y-2">
              {activeBooking.status === 'accepted' && (
                <button onClick={() => handleTripAction('arriving')} disabled={actionLoading} className="btn-primary w-full">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "I'm On My Way"}
                </button>
              )}
              {activeBooking.status === 'driver_arriving' && (
                <button onClick={() => handleTripAction('arrived')} disabled={actionLoading} className="btn-primary w-full">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "I've Arrived"}
                </button>
              )}
              {activeBooking.status === 'driver_arrived' && (
                <button onClick={() => handleTripAction('start_trip')} disabled={actionLoading} className="btn-primary w-full">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Start Trip'}
                </button>
              )}
              {activeBooking.status === 'in_progress' && (
                <>
                  {activeBooking.dropoff_lat && activeBooking.dropoff_lng && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${activeBooking.dropoff_lat},${activeBooking.dropoff_lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium border"
                      style={{ background: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.3)', color: 'var(--info)' }}
                    >
                      <Navigation className="w-4 h-4" />Navigate to Drop-off
                    </a>
                  )}
                  <button
                    onClick={() => handleTripAction('complete_trip')}
                    disabled={actionLoading}
                    className="w-full py-3 rounded-xl text-sm font-semibold"
                    style={{ background: 'var(--success)', color: '#fff' }}
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : '✓ Complete Trip'}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {/* ── EARNINGS ───────────────────────────────────────────────────── */}
        {view === 'earnings' && (
          <>
            <div>
              <h2 className="text-xl font-bold">Earnings & History</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Your trip history</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 rounded-xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                <p className="text-2xl font-bold" style={{ color: 'var(--success)' }}>£{todayEarnings.toFixed(2)}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Today</p>
              </div>
              <div className="p-4 rounded-xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                <p className="text-2xl font-bold" style={{ color: 'var(--primary)' }}>£{Number(driver.total_earnings).toFixed(2)}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>All Time</p>
              </div>
            </div>
            <div className="space-y-2">
              {tripHistory.map((trip) => (
                <div
                  key={trip.id}
                  className="p-3 rounded-xl border flex items-center gap-3"
                  style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background: trip.status === 'completed' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                    }}
                  >
                    <Car className="w-4 h-4" style={{ color: trip.status === 'completed' ? 'var(--success)' : 'var(--danger)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{trip.pickup_address}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{trip.dropoff_address}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold" style={{ color: trip.status === 'completed' ? 'var(--success)' : 'var(--muted-foreground)' }}>
                      {trip.status === 'completed' ? `£${Number(trip.final_fare || trip.estimated_fare || 0).toFixed(2)}` : 'Cancelled'}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {trip.completed_at
                        ? new Date(trip.completed_at).toLocaleDateString()
                        : new Date(trip.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
              {tripHistory.length === 0 && (
                <div className="text-center py-8">
                  <Car className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No trips yet</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bottom Nav */}
      <div
        className="sticky bottom-0 border-t grid grid-cols-4"
        style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {[
          { id: 'dashboard', icon: Car, label: 'Home' },
          { id: 'jobs', icon: Bell, label: 'Jobs', badge: pendingJobs.length },
          { id: 'active_trip', icon: Navigation, label: 'Trip' },
          { id: 'earnings', icon: DollarSign, label: 'Earnings' },
        ].map(({ id, icon: Icon, label, badge }) => (
          <button
            key={id}
            onClick={() => setView(id as DriverView)}
            className="py-3 flex flex-col items-center gap-1 relative transition-all"
            style={{ color: view === id ? 'var(--primary)' : 'var(--muted-foreground)' }}
          >
            <div className="relative">
              <Icon className="w-5 h-5" />
              {badge && badge > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-xs flex items-center justify-center font-bold"
                  style={{ background: 'var(--danger)', color: '#fff' }}
                >
                  {badge}
                </span>
              )}
            </div>
            <span className="text-xs">{label}</span>
          </button>
        ))}
      </div>

      {/* Confirmation Modal */}
      {modal && (
        <ConfirmModal
          type={modal.type}
          job={modal.job}
          onConfirm={() => handleJobResponse(modal.job, modal.type === 'accept' ? 'accepted' : 'rejected')}
          onCancel={() => setModal(null)}
          loading={actionLoading}
        />
      )}

      {/* In-Ride Chat */}
      {activeBooking && (
        <RideChat
          bookingId={activeBooking.id}
          myRole="driver"
          pickupAddress={activeBooking.pickup_address}
          dropoffAddress={activeBooking.dropoff_address}
          otherPartyName={activeBooking.passengers?.full_name || 'Passenger'}
          isActiveRide={['accepted', 'driver_arriving', 'driver_arrived', 'in_progress'].includes(activeBooking.status)}
        />
      )}
    </div>
  );
}
